import { ReactNode } from 'react';
import { Button } from 'ui/button';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button/constants';
import { Modal, ModalHeader, ModalBody, MODAL_SIZES, MODAL_VARIANTS, ModalFooter } from 'ui/modal';
import noOp from 'utils/noop';
import { useDeleteSnippet } from '../../hooks/useDeleteSnippet';
import { useSnippetQueryContext } from '../../context/SnippetQueryContext';
import { GraphQLSnippet } from '../../types';
import { ModalIntent } from '../../constants';

type ModalContent = {
  header: string;
  bodyMsg: (snippetName: string) => ReactNode | string;
  buttonText: string;
};

const modalContent: ModalContent = {
  header: 'Delete snippet?',
  bodyMsg: (snippetName: string) => (
    <span>
      Any templates that use <strong>{snippetName}</strong> won&apos;t send until the snippet is
      removed. This cannot be undone.
    </span>
  ),
  buttonText: 'Delete snippet',
};

type Props = {
  closeModal: () => void;
  intent?: ModalIntent;
  onDeleteSuccess: () => void;
  snippet: GraphQLSnippet;
};

export const DeleteSnippetModal = ({ closeModal, intent, onDeleteSuccess, snippet }: Props) => {
  const { refetch } = useSnippetQueryContext();

  const [deleteSnippet] = useDeleteSnippet({
    onComplete: () => {
      closeModal();
    },
    onError: noOp,
    onSuccess: () => {
      if (intent === ModalIntent.edit) {
        onDeleteSuccess();
      } else {
        refetch();
      }
    },
  });

  const handleDeleteSnippet = () => {
    /** Deletes the snippet */
    deleteSnippet({
      variables: {
        ids: [snippet.id],
      },
    });
  };

  return (
    <Modal
      data-test="snippet-delete-modal"
      id="deleteSnippetModal"
      isModalOpen={true}
      onClose={closeModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
    >
      <ModalHeader autoFocus={false}>{modalContent.header}</ModalHeader>

      <ModalBody>{modalContent.bodyMsg(snippet.name)}</ModalBody>

      <ModalFooter>
        <Button
          onClick={handleDeleteSnippet}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.DESTRUCTIVE}
        >
          {modalContent.buttonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
